/**
 * The credit-request queue (§18.6): a request must not be lost
 * between two people's inboxes.
 *
 * `Queue` is pure -- it sorts and renders a list already resolved to
 * `QueueRow`s, so the ordering and column tests exercise it with no network
 * at all. `QueueRoute` is what `./routes.ts` actually mounts: it fetches the
 * default filter (`OPEN` and `IN_REVIEW` together -- excluding `IN_REVIEW`
 * would hide what a colleague already claimed, which loses the request by
 * another route) and joins each request to its tenant's balance from the
 * register (`useOpsTenants`), because "how many do they want" is not
 * answerable without "how many do they have" and this plane has no
 * `credit_request`-scoped balance endpoint of its own to ask instead.
 *
 * `ResolvedRequests` (Figma 158:4454, "Resolvidos") is new: the frame shows a
 * table of decided requests below the open queue, which the screen never had
 * before. It reuses the same `useCreditRequests` hook with `RESOLVED_STATES`
 * -- no new endpoint -- and scopes to the last 30 days client-side, matching
 * the frame's "últimos 30 dias" badge with no new query parameter.
 * `WITHDRAWN` is left out: it has no `decided_by`, so "QUEM FECHOU" would be
 * blank for a row nobody closed.
 */

import { Inbox } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';

import { Badge } from '@/ui/badge';
import { ItemGroup, ItemSeparator } from '@/ui/item';
import { EmptyState, QueryStates } from '../../chrome/state';
import { Page, PageHeader, PageSection } from '../../chrome/page';
import { DataTable } from '../../chrome/table';
import { defineCatalogue, getLocale, type Locale } from '../../i18n';
import { integer } from '../../i18n/format';
import { useCreditRequests, useOpsTenants, opsKeys } from '../api/queries';
import type { CreditRequest } from '../api/schemas';
import { formatDate } from '../lib/date';
import { Credits } from '../lib/units';
import { opsTitles } from '../shell/titles';
import { RequestCard } from './RequestCard';

const catalogue = defineCatalogue(
  {
    'queue.section.title': 'Na fila · créditos e vagas',
    'queue.count.other': '{count} pedidos',
    'queue.count.one': '{count} pedido',
    'queue.empty.title': 'Sem pedidos abertos',
    'queue.empty.body':
      'As organizações pedem pela tela de créditos delas; volte aqui para conferir, pois nada notifica você.',
    'resolved.title': 'Resolvidos',
    'resolved.window': 'últimos 30 dias',
    'resolved.empty.title': 'Nenhum pedido resolvido nos últimos 30 dias',
    'resolved.header.tenant': 'Organização',
    'resolved.header.request': 'Pedido',
    'resolved.header.outcome': 'Desfecho',
    'resolved.header.closedBy': 'Quem fechou',
    'resolved.header.when': 'Quando',
    'resolved.outcome.GRANTED': 'Concedido',
    'resolved.outcome.DECLINED': 'Recusado',
    'resolved.slots.other': '{count} vagas',
    'resolved.slots.one': '{count} vaga',
    'resolved.slotsNotSpecified': 'vagas não informadas',
  },
  {
    'queue.section.title': 'In the queue · credits and slots',
    'queue.count.other': '{count} requests',
    'queue.count.one': '{count} request',
    'queue.empty.title': 'No open requests',
    'queue.empty.body':
      'Organisations ask from their credits screen; check back here, since nothing notifies you.',
    'resolved.title': 'Resolved',
    'resolved.window': 'last 30 days',
    'resolved.empty.title': 'No requests resolved in the last 30 days',
    'resolved.header.tenant': 'Organisation',
    'resolved.header.request': 'Request',
    'resolved.header.outcome': 'Outcome',
    'resolved.header.closedBy': 'Closed by',
    'resolved.header.when': 'When',
    'resolved.outcome.GRANTED': 'Granted',
    'resolved.outcome.DECLINED': 'Declined',
    'resolved.slots.other': '{count} slots',
    'resolved.slots.one': '{count} slot',
    'resolved.slotsNotSpecified': 'slots not specified',
  },
);
const t = catalogue.t;

/** The states the queue shows by default. Exported so `QueueRoute`'s test can
 *  assert the fetch it triggers without re-typing the literal. */
export const DEFAULT_STATES = ['OPEN', 'IN_REVIEW'];

/** A decided request has `decided_by` set; `WITHDRAWN` never does, so it is
 *  left out of "who closed it" (see file header). */
export const RESOLVED_STATES = ['GRANTED', 'DECLINED'];

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

function isWithinLast30Days(isoDate: string): boolean {
  return Date.now() - new Date(isoDate).getTime() <= THIRTY_DAYS_MS;
}

export interface QueueRow {
  request: CreditRequest;
  /** `null` when the register has not resolved a balance for this tenant.
   *  `balance_credits` is not nullable on the wire, so this absence is the
   *  register's, not the organisation's, and `RequestCard` says so rather
   *  than rendering it as a credit figure at all. */
  tenantBalance: number | null;
}

export function Queue(props: {
  requests: QueueRow[];
  onChanged?: (request: CreditRequest) => void;
}): ReactNode {
  // Oldest first: age is the figure the queue is about, and it is also the
  // order the `open_queue` endpoint returns.
  const sorted = [...props.requests].sort(
    (a, b) => new Date(a.request.created_at).getTime() - new Date(b.request.created_at).getTime(),
  );

  return (
    <ItemGroup className="gap-0 rounded-lg border">
      {sorted.map((row, index) => (
        // A `Fragment`, not a wrapping element: `ItemGroup` carries
        // `role="list"`, which ARIA requires to contain only `listitem`s --
        // an intervening `<div>` here would put a non-`listitem` between
        // them, same failure shape as `Item` without `asListItem`.
        <Fragment key={row.request.id}>
          {index > 0 && <ItemSeparator />}
          <RequestCard
            request={row.request}
            tenantBalance={row.tenantBalance}
            onChanged={props.onChanged}
            asListItem
          />
        </Fragment>
      ))}
    </ItemGroup>
  );
}

/** The requested-amount cell of the "Resolvidos" table, in the request's own
 *  kind -- a resolved `SLOTS` request has no `credits_requested`, so this
 *  reads whichever amount its `kind` actually carries. */
function resolvedRequestAmount(request: CreditRequest, locale: Locale): ReactNode {
  if (request.kind === 'SLOTS') {
    return request.slots_requested === null
      ? t('resolved.slotsNotSpecified')
      : catalogue.plural('resolved.slots.other', request.slots_requested, {
          count: integer(request.slots_requested, locale),
        });
  }
  return <Credits value={request.credits_requested} unit="tenant" />;
}

/** The "Resolvidos" table (Figma 158:4454): who asked, what they asked for,
 *  what was decided, who decided it, and when. Pure -- `QueueRoute` owns the
 *  fetch and the 30-day scoping, this just renders the resulting list. */
export function ResolvedRequests(props: { requests: CreditRequest[] }): ReactNode {
  const locale = getLocale();
  const columns: ColumnDef<CreditRequest, unknown>[] = [
    {
      accessorKey: 'tenant_name',
      header: t('resolved.header.tenant'),
    },
    {
      id: 'requested_amount',
      header: t('resolved.header.request'),
      meta: { numeric: true },
      cell: (info) => (
        <span data-testid="resolved-request-amount">
          {resolvedRequestAmount(info.row.original, locale)}
        </span>
      ),
    },
    {
      id: 'outcome',
      header: t('resolved.header.outcome'),
      cell: (info) => {
        const request = info.row.original;
        const outcome = request.state as 'GRANTED' | 'DECLINED';
        const label = t(`resolved.outcome.${outcome}`);
        const note = request.decision_note;
        return (
          <Badge
            data-testid="resolved-outcome"
            variant={outcome === 'GRANTED' ? 'default' : 'destructive'}
          >
            {note !== null && note.trim() !== '' ? `${label} · ${note}` : label}
          </Badge>
        );
      },
    },
    {
      id: 'decided_by',
      header: t('resolved.header.closedBy'),
      cell: (info) => info.row.original.decided_by,
    },
    {
      id: 'decided_at',
      header: t('resolved.header.when'),
      cell: (info) => {
        const decidedAt = info.row.original.decided_at;
        return decidedAt !== null ? formatDate(decidedAt, locale) : null;
      },
    },
  ];

  return (
    <DataTable<CreditRequest>
      columns={columns}
      data={props.requests}
      getRowId={(row) => row.id}
      caption={t('resolved.title')}
      emptyState={<EmptyState title={t('resolved.empty.title')} />}
    />
  );
}

function resolvedWithinWindow(requests: CreditRequest[]): CreditRequest[] {
  return requests.filter((request) =>
    request.decided_at !== null ? isWithinLast30Days(request.decided_at) : false,
  );
}

/** The route element `./routes.ts` registers. Owns the three live queries and
 *  their joins; `Queue` and `ResolvedRequests` above stay free of all of them
 *  so each can be tested as a pure list. The tenant register is joined
 *  best-effort: while it is still resolving, every balance reads as absent
 *  rather than blocking the queue on a second query it does not itself need
 *  to load. */
export function QueueRoute(): ReactNode {
  const requestsQuery = useCreditRequests(DEFAULT_STATES);
  const resolvedQuery = useCreditRequests(RESOLVED_STATES);
  const tenantsQuery = useOpsTenants();
  const queryClient = useQueryClient();

  return (
    <Page>
      <PageHeader title={opsTitles.t('requests')} />

      <PageSection
        title={t('queue.section.title')}
        actions={
          requestsQuery.data !== undefined && (
            <Badge variant="secondary">
              {catalogue.plural('queue.count.other', requestsQuery.data.length, {
                count: integer(requestsQuery.data.length, getLocale()),
              })}
            </Badge>
          )
        }
      >
        <QueryStates
          query={requestsQuery}
          empty={
            <EmptyState
              icon={Inbox}
              title={t('queue.empty.title')}
              description={t('queue.empty.body')}
            />
          }
        >
          {(requests) => {
            const balanceByTenant = new Map(
              (tenantsQuery.data ?? []).map((tenant) => [tenant.tenant_id, tenant.balance_credits]),
            );
            const rows: QueueRow[] = requests.map((request) => ({
              request,
              tenantBalance: balanceByTenant.get(request.tenant_id) ?? null,
            }));
            return (
              <Queue
                requests={rows}
                onChanged={() =>
                  queryClient.invalidateQueries({ queryKey: opsKeys.requests(DEFAULT_STATES) })
                }
              />
            );
          }}
        </QueryStates>
      </PageSection>

      <PageSection
        title={t('resolved.title')}
        actions={<Badge variant="secondary">{t('resolved.window')}</Badge>}
      >
        <QueryStates
          query={resolvedQuery}
          isEmpty={(data) => resolvedWithinWindow(data).length === 0}
          empty={<EmptyState title={t('resolved.empty.title')} />}
        >
          {(requests) => <ResolvedRequests requests={resolvedWithinWindow(requests)} />}
        </QueryStates>
      </PageSection>
    </Page>
  );
}
